import { watch, type Ref } from 'vue';

interface DateFilters {
  fromDate: string;
  toDate: string;
}

// The last day of the month of a YYYY-MM-DD date. setUTCFullYear keeps years
// below 100 literal (the Date constructor maps 20 to 1920, and a year typed digit
// by digit passes through 0002 and 0020), and day 0 of the next month is this
// month's last day.
function endOfMonthOf(date: string): string {
  const [year, month] = date.split('-').map(Number) as [number, number];
  const lastDay = new Date(0);
  lastDay.setUTCFullYear(year, month, 0);

  return lastDay.toISOString().split('T')[0] ?? '';
}

function startOfMonthOf(date: string): string {
  return date.replace(/-\d+$/, '-01');
}

// Keeps a Desde/Hasta pair from crossing and from silently widening. Cobros and
// Pagos load every shift of the period on the server, so moving Desde back
// (stepping it month by month toward the month that matters) pulls Hasta to the
// end of Desde's month instead of growing the period at every step (2026-10-27
// incident). A wider period stays one deliberate Hasta change away.
export function useLinkedDateRange(filters: Ref<DateFilters>) {
  watch(() => filters.value.fromDate, (fromDate, previousFromDate) => {
    if (!fromDate) return;

    const endOfMonth = endOfMonthOf(fromDate);
    const movedBack = Boolean(previousFromDate) && fromDate < previousFromDate;
    if (fromDate > filters.value.toDate || (movedBack && filters.value.toDate > endOfMonth)) {
      filters.value.toDate = endOfMonth;
    }
  });

  watch(() => filters.value.toDate, (toDate) => {
    if (toDate && toDate < filters.value.fromDate) {
      filters.value.fromDate = startOfMonthOf(toDate);
    }
  });
}
